import React, { useState, useEffect, useContext, useMemo } from "react";
import { PatientIDContext } from "../../Contexts";
import { getAllTrialsByUser, getTrials } from "../../util/reportAPI";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import "../../../dist/assets/stylesheets/css/recordPatient.css";
import "../../../dist/assets/stylesheets/css/patients.css";
import Select from 'react-select';
import RecordingsRow from "./RecordingsRow";
import {
  mergeTrialPollUpdate,
  shouldClearPendingProcessPoll,
  trialNeedsStatusPolling,
} from "../../util/trialStatusDisplay";
import { compareQualityValues, getCalibrationQuality, getTrackingQuality } from "../../util/trialQuality";
import { compareTrialIds, compareTrialsByRecordedAt } from "../../util/trialSort";
import ConfirmModal from "../modals/ConfirmModal";
import GlobalOverlayPortal from "../modals/GlobalOverlayPortal";
import { deleteTrial, trashTrials, pollTrialsStatus, updateTrialMetadata } from "../../util/trialApi";
import { getPatients } from "../../util/patientsApi";
import { useDispatch } from "react-redux";
import { notificationActions } from "../../store/notification-slice";

function compareRecordings(a, b, sortingQuery, sortingAscending, qualityAccessors) {
  const accessor = qualityAccessors[sortingQuery];
  if (accessor) return compareQualityValues(accessor(a), accessor(b), sortingAscending);
  // Same recorded second (the clock time on screen) lists the higher trial id first.
  if (sortingQuery === "date") return compareTrialsByRecordedAt(a, b, sortingAscending);
  if (sortingQuery === "id") return compareTrialIds(a, b, !sortingAscending);
  return sortingAscending
    ? String(a[sortingQuery]).localeCompare(String(b[sortingQuery]))
    : String(b[sortingQuery]).localeCompare(String(a[sortingQuery]));
}

function RecordingsTable(props) {
  const patientID = useContext(PatientIDContext);
  const dispatch = useDispatch();
  const statusVals = [{ label: 'All', value: 'all' }, { label: 'Ready', value: 'ready' }, { label: 'Complete', value: 'complete' }, { label: 'Uploading', value: 'uploading' }, { label: 'Processing', value: 'processing' }];
  const [trials, setTrials] = useState(props.trials);
  const [sortingQuery, setSortingQuery] = useState("date");
  const [sortingAscending, setSortingAscending] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [query, setQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [pendingProcessIds, setPendingProcessIds] = useState(() => new Set());
  const [selectedTrialIds, setSelectedTrialIds] = useState(new Set());
  const [trashConfirmOpen, setTrashConfirmOpen] = useState(false);
  const [isTrashing, setIsTrashing] = useState(false);
  const [reassignTrial, setReassignTrial] = useState(null);
  const [reassignPatientId, setReassignPatientId] = useState("");
  const [reassignPatients, setReassignPatients] = useState([]);
  const [reassignPatientsLoading, setReassignPatientsLoading] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const search_keys = ["id", "patient_name"];
  // Quality columns hold measurements, so they sort numerically rather than by
  // the string comparison the other columns use.
  const qualityAccessors = {
    calib_worst_cam_loo_px: (trial) => getCalibrationQuality(trial).value,
    pose_cam_support: (trial) => getTrackingQuality(trial).value,
  };
  const search = (data) => {
    if (!data) return [];
    let filtered = data.filter((obj) =>
      search_keys.some((key) =>
        String(obj[key]).toLowerCase().includes(query.toLowerCase())
      )
    ).sort((a, b) => compareRecordings(a, b, sortingQuery, sortingAscending, qualityAccessors));
    if (selectedStatus != 'all') {
      filtered = filtered.filter(trial => trial.status == selectedStatus);
    }
    return filtered;
  };
  const filteredTrials = search(trials);
  const totalRows = filteredTrials.length;
  const paginatedTrials = filteredTrials.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setIsRefreshing(false);
    setTrials(props.trials);
    setPendingProcessIds(new Set());
  }, [props.trials]);
  useEffect(() => {
    setPage(0);
  }, [query, selectedStatus, rowsPerPage]);

  // Poll trial status for processing (and uploading) so progress bar updates.
  // One batch request per tick — avoids N parallel get-trial-status calls (browser connection limit).
  const processingIdsKey = useMemo(() => {
    const list = Array.isArray(trials) ? trials : [];
    return list
      .filter((t) => trialNeedsStatusPolling(t.status))
      .map((t) => t.id)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(',');
  }, [trials]);

  useEffect(() => {
    if (!processingIdsKey) return;
    const processingIds = processingIdsKey.split(',').filter(Boolean);

    const pollStatuses = async () => {
      try {
        const batch = await pollTrialsStatus(processingIds);
        const results = Array.isArray(batch?.result) ? batch.result : [];
        const byId = new Map(results.map((r) => [String(r.id), r]));

        setTrials((prev) => {
          const nextList = Array.isArray(prev) ? prev : [];
          return nextList.map((row) => mergeTrialPollUpdate(row, byId.get(String(row.id))));
        });

        setPendingProcessIds((prev) => {
          const next = new Set(prev);
          results.forEach((row) => {
            if (shouldClearPendingProcessPoll(row)) next.delete(row.id);
          });
          return next;
        });
      } catch (err) {
        console.error("Error polling trial status:", err);
      }
    };

    pollStatuses();
    const intervalId = setInterval(pollStatuses, 3000);
    return () => clearInterval(intervalId);
  }, [processingIdsKey]);

  const refresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      props.refreshHandler();
    }, 2000);
  };
  const handleSetProcessing = (trialId) => {
    setPendingProcessIds((prev) => new Set(prev).add(trialId));
    const list = Array.isArray(trials) ? trials : [];
    const copy = list.map((t) =>
      t.id === trialId ? { ...t, status: "queued", progress: 0 } : t
    );
    setTrials(copy);
  };
  const handleDeleteRequest = (trialId) => {
    setDeleteConfirmId(trialId);
  };
  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      deleteTrial(deleteConfirmId).then(() => {
        props.refreshHandler();
        setDeleteConfirmId(null);
      }).catch(() => setDeleteConfirmId(null));
    }
  };

  const toggleTrialSelection = (id) => {
    setSelectedTrialIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    const pageIds = paginatedTrials.map((t) => t.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedTrialIds.has(id));
    if (allSelected) {
      setSelectedTrialIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedTrialIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const selectAllChecked = paginatedTrials.length > 0 && paginatedTrials.every((t) => selectedTrialIds.has(t.id));
  const selectAllIndeterminate = paginatedTrials.some((t) => selectedTrialIds.has(t.id)) && !selectAllChecked;

  const openReassign = (trial) => {
    setReassignTrial(trial);
    setReassignPatientId(String(trial.patient_id ?? ""));
    setReassignPatientsLoading(true);
    getPatients()
      .then((list) => {
        setReassignPatients(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setReassignPatients([]);
        dispatch(
          notificationActions.setNotification({
            show: true,
            status: "error",
            message: "Failed to load patients.",
          })
        );
      })
      .finally(() => setReassignPatientsLoading(false));
  };
  const closeReassign = () => {
    setReassignTrial(null);
    setReassignPatientId("");
    setReassignPatients([]);
  };
  const saveReassign = () => {
    if (!reassignTrial) return;
    if (String(reassignPatientId) === String(reassignTrial.patient_id)) return;
    setReassignSaving(true);
    updateTrialMetadata(reassignTrial.id, reassignPatientId)
      .then(() => {
        dispatch(
          notificationActions.setNotification({
            show: true,
            status: "success",
            message: "Recording reassigned to the selected patient.",
          })
        );
        closeReassign();
        props.refreshHandler();
      })
      .catch((err) => {
        dispatch(
          notificationActions.setNotification({
            show: true,
            status: "error",
            message: err?.message || "Failed to reassign recording.",
          })
        );
      })
      .finally(() => setReassignSaving(false));
  };

  const handleTrashConfirm = () => {
    const ids = Array.from(selectedTrialIds);
    if (ids.length === 0) return;
    setIsTrashing(true);
    trashTrials(ids)
      .then(() => {
        setSelectedTrialIds(new Set());
        setTrashConfirmOpen(false);
        props.refreshHandler();
        dispatch(notificationActions.setNotification({ show: true, status: "success", message: "Moved to trash." }));
      })
      .catch((err) => {
        dispatch(
          notificationActions.setNotification({
            show: true,
            status: "error",
            message: err?.response?.data?.reason || err?.message || "Failed to move to trash.",
          })
        );
      })
      .finally(() => setIsTrashing(false));
  };

  const renderSortControl = (field, defaultAscending = true) => {
    if (sortingQuery == field) {
      return sortingAscending ? (
        <Icon
          onClick={() => setSortingAscending(false)}
          icon="fluent:arrow-sort-down-24-filled"
        />
      ) : (
        <Icon
          onClick={() => setSortingAscending(true)}
          icon="fluent:arrow-sort-down-24-filled"
          vFlip={true}
        />
      );
    }
    return (
      <Icon
        onClick={() => {
          setSortingQuery(field);
          setSortingAscending(defaultAscending);
        }}
        icon="basil:sort-solid"
      />
    );
  };

  return (
    <section className="patient-table container recordings">
      <div className="selection-toolbar-slot">
        {selectedTrialIds.size > 0 && (
          <div className="selection-toolbar">
            <span className="selection-toolbar__count">{selectedTrialIds.size} trial(s) selected</span>
            <button type="button" className="svg-btn selection-toolbar__btn" onClick={() => setTrashConfirmOpen(true)}>
              Move to trash
            </button>
            <button type="button" className="svg-btn selection-toolbar__btn selection-toolbar__btn--secondary" onClick={() => setSelectedTrialIds(new Set())}>
              Clear selection
            </button>
          </div>
        )}
      </div>
      <div className="flex search-tool">
        <div style={{ display: 'flex' }}>
          <input
            id="search-bar"
            placeholder="Search..."
            onChange={(e) => setQuery(e.target.value)}
          ></input>
          <Select
            styles={{
              control: (base) => ({
                ...base,
                width: '150px',
                marginLeft: '10px',
                border: '1px solid #ddd'
              })
            }}
            onChange={(e) => setSelectedStatus(e.value)} options={statusVals} defaultValue={{ label: 'All', value: 'all' }} />
        </div>
        <div style={{ display: 'flex' }}>
          <button className={"refresh-btn svg-btn " + (isRefreshing ? 'refreshing' : '')} onClick={refresh}>
            <Icon icon="tabler:refresh" />
            Refresh</button>
          <Link to="/record-test">
            <button className="svg-btn new-recording-btn">
              <Icon icon="fluent:add-16-filled" />
              Create new recording
            </button>
          </Link>
        </div>
      </div>
      {
        <table>
          <thead>
            <tr>
              <th className="table-header-checkbox">
                <input
                  type="checkbox"
                  checked={selectAllChecked}
                  ref={(el) => el && (el.indeterminate = selectAllIndeterminate)}
                  onChange={selectAllOnPage}
                  aria-label="Select all on this page"
                />
              </th>
              <th>
                Trial ID
                {renderSortControl("id")}
              </th>
              <th>
                Patient Name
                {renderSortControl("patient_name")}
              </th>
              <th>
                Date
                {renderSortControl("date", false)}
              </th>
              <th>Status</th>
              <th>
                Calibration Quality
                {renderSortControl("calib_worst_cam_loo_px")}
              </th>
              <th>
                Tracking Quality
                {renderSortControl("pose_cam_support", false)}
              </th>
              <th>Tags</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {totalRows === 0 ? (
              <tr>
                <td colSpan={9} className="recordings-table-empty">
                  Your session data will appear here
                </td>
              </tr>
            ) : (
              paginatedTrials.map((trial) => (
                <RecordingsRow
                  key={trial.id}
                  handleView={props.handleView}
                  setProcessing={() => handleSetProcessing(trial.id)}
                  onDeleteRequest={handleDeleteRequest}
                  onReassignRequest={openReassign}
                  trial={trial}
                  selected={selectedTrialIds.has(trial.id)}
                  onToggle={toggleTrialSelection}
                  pendingProcess={pendingProcessIds.has(trial.id)}
                />
              ))
            )}
          </tbody>
        </table>
      }
      <div className="table-pagination">
        <span>Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="pagination-range">
          {totalRows === 0 ? '0-0 of 0' : `${page * rowsPerPage + 1}-${Math.min(page * rowsPerPage + rowsPerPage, totalRows)} of ${totalRows}`}
        </span>
        <div className="pagination-actions">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(Math.ceil(totalRows / rowsPerPage) - 1, p + 1))}
            disabled={page >= Math.ceil(totalRows / rowsPerPage) - 1 || totalRows === 0}
            aria-label="Next page"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
      {deleteConfirmId != null && (
        <ConfirmModal
          message="Are you sure you want to delete this recording?"
          acceptHandler={handleDeleteConfirm}
          rejectHandler={() => setDeleteConfirmId(null)}
        />
      )}
      {trashConfirmOpen && (
        <ConfirmModal
          message={`Move ${selectedTrialIds.size} trial(s) to trash? They will be permanently deleted after 30 days.`}
          acceptHandler={handleTrashConfirm}
          rejectHandler={() => !isTrashing && setTrashConfirmOpen(false)}
          isLoading={isTrashing}
          confirmLabel={isTrashing ? undefined : "Move to trash"}
        />
      )}
      {reassignTrial && (
        <GlobalOverlayPortal>
          <div className="modal-container">
            <div className="modal confirm reassign-trial-modal">
              <Icon
                onClick={() => { if (!reassignSaving) closeReassign(); }}
                className="close-btn"
                icon="iconamoon:close"
                style={reassignSaving ? { pointerEvents: "none", opacity: 0.5 } : undefined}
                aria-hidden={reassignSaving}
              />
              <div className="icon-info-container">
                <div className="icon-container">
                  <Icon icon="mdi:account-switch" />
                </div>
                <div className="info-container">
                  <h4>Change patient</h4>
                  <span>
                    Assign trial <strong>{reassignTrial.id}</strong> to another patient. The recording will appear under that patient&apos;s list.
                  </span>
                  {reassignPatientsLoading ? (
                    <p className="reassign-trial-modal__status">Loading patients…</p>
                  ) : (
                    <div className="reassign-trial-modal__field">
                      <label htmlFor="reassign-patient-select">Patient</label>
                      <select
                        id="reassign-patient-select"
                        className="reassign-trial-modal__select"
                        value={reassignPatientId}
                        onChange={(e) => setReassignPatientId(e.target.value)}
                        disabled={reassignSaving}
                      >
                        {reassignPatients
                          .slice()
                          .sort((a, b) => {
                            const an = (a.patient_name || `Patient ${a.patient_id}`).toLowerCase();
                            const bn = (b.patient_name || `Patient ${b.patient_id}`).toLowerCase();
                            return an.localeCompare(bn);
                          })
                          .map((p) => {
                            const label = p.patient_name && String(p.patient_name).trim()
                              ? p.patient_name
                              : `Patient ${p.patient_id}`;
                            return (
                              <option key={p.patient_id} value={String(p.patient_id)}>
                                {label}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="btn-container">
                <button type="button" id="reassign-cancel-btn" onClick={closeReassign} disabled={reassignSaving}>
                  Cancel
                </button>
                <button
                  type="button"
                  id="reassign-save-btn"
                  onClick={saveReassign}
                  disabled={
                    reassignSaving ||
                    reassignPatientsLoading ||
                    reassignPatients.length === 0 ||
                    String(reassignPatientId) === String(reassignTrial.patient_id)
                  }
                >
                  {reassignSaving ? (
                    <>
                      <Icon icon="eos-icons:loading" className="confirm-loading-spinner" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </GlobalOverlayPortal>
      )}
    </section>
  );
}

export default RecordingsTable;
